import { useMediaQuery } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import React, { useEffect } from "react";
import { NavTabs } from "./NavTabs";
import { theme } from "@/theme";
import { MobileNav } from "./MobileNav";
import { CartIcon } from "./CartIcon";
import { useCartUiActions } from "../cart";
import { NavData } from "./NavItem";
import { Button } from "@/components/Button";
import {
  useMobileSearchEnabled,
  useSearchEnabled,
  useUiActions,
} from "@/uiState";

export interface NavHeaderProps {
  cartCount: string;
  customerName?: string;
  accountLink: string;
  navigationData: {
    leftDesktopNavData: NavData[];
    rightDesktopNavData: NavData[];
    mobileNavData: NavData[];
  };
}

const NavHeader = ({
  cartCount,
  customerName,
  accountLink,
  navigationData,
}: NavHeaderProps) => {
  const uiActions = useUiActions();
  const searchEnabled = useSearchEnabled();
  const mobileSearchEnabled = useMobileSearchEnabled();
  const cartActions = useCartUiActions();
  const mobile: boolean = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.lg)
  );

  useEffect(() => {
    const root = document.documentElement;
    const header: HTMLElement | null = document.querySelector(".js-header");

    const updateStyles = () => {
      if (header) {
        const headerHeight = header.offsetHeight;
        root.style.setProperty("--header-height", `${headerHeight}px`);
      }
    };

    // Call the updateStyles function on initial load
    updateStyles();

    // Check if ResizeObserver is supported
    if (typeof ResizeObserver === "undefined") {
      console.warn("ResizeObserver is not supported in this browser.");
      return;
    }

    // Update styles when js-header height changes
    const resizeObserver = new ResizeObserver(updateStyles);
    if (header) {
      resizeObserver.observe(header);
    }

    // Cleanup the observer on component unmount
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const accountLinks = {
    type: "submenu",
    title: "Account",
    links: customerName
      ? [
          {
            type: "link",
            title: "My Account",
            href: accountLink,
          },
          {
            type: "link",
            title: "My Rewards",
            href: "/pages/rewards-program",
          },
          {
            type: "link",
            title: "Sign Out",
            href: "/account/logout",
          },
        ]
      : [
          {
            type: "link",
            title: "Sign In",
            href: "/account/login",
          },
        ],
  } satisfies NavData;

  const logo = (
    <div className="flex basis-4/12 items-center justify-center p-2 lg:basis-auto lg:justify-start lg:pl-0 lg:pr-8">
      <a href="/" className="flex items-center">
        <img
          className="h-auto w-20 lg:w-24"
          alt="dropps logo"
          loading="eager"
          src={
            "https://cdn.shopify.com/s/files/1/0268/4219/files/dropps-logo-2024-blue.svg?v=1715968073"
          }
          width="80"
          height="32"
        />
      </a>
    </div>
  );

  return (
    <>
      <div className="relative top-0.5 mx-auto flex w-full max-w-screen-xl items-center px-1 lg:px-14">
        {/* left */}
        <div className="flex basis-4/12 lg:grow lg:basis-auto">
          {mobile ? (
            <MobileNav
              customerName={customerName}
              accountLink={accountLink}
              mobileNavdata={navigationData.mobileNavData}
            />
          ) : (
            <>
              {logo}
              <NavTabs
                links={navigationData.leftDesktopNavData}
                rootSelector={"nav-tabs-dropps"}
                tabSelector={"nav-tab-"}
                panelSelector={"nav-tabpanel-"}
                ariaLabel={"nav tabs"}
                minTabWidth={"unset"}
                minWidth={"0px"}
                maxWidth={"455px"}
                noGap={false}
                align={"left"}
              />
            </>
          )}
        </div>
        {/* center */}
        {mobile && logo}
        {/* right */}
        <div className="flex flex-grow basis-4/12 items-center justify-end lg:basis-auto">
          {mobile && searchEnabled ? (
            <>
              <form
                action="/pages/search-results-page"
                method="get"
                role="search"
                className="hidden lg:flex"
              >
                <input
                  className="w-[200px] rounded-none border-0 border-b border-primary"
                  type="search"
                  name="q"
                  placeholder="Search"
                  aria-label="Search"
                  autoComplete="off"
                />
                <Button variant="ghost" type="submit" className="pl-1">
                  <SearchIcon />
                  <span className="sr-only">Submit</span>
                </Button>
              </form>
            </>
          ) : null}

          {!mobile && (
            <NavTabs
              links={[...navigationData.rightDesktopNavData, accountLinks]}
              rootSelector={"account-tabs-dropps"}
              tabSelector={"sign-in-tab-"}
              panelSelector={"sign-in-tab-panel-"}
              ariaLabel={"sign in tabs"}
              minTabWidth={"unset"}
              noGap={false}
            />
          )}
          <CartIcon
            className="ml-2"
            count={+cartCount}
            onClick={cartActions.openDrawer}
          />
        </div>
      </div>
    </>
  );
};

export default NavHeader;
